import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { approveDealExtension, denyDealExtension, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { ChannelPartnerSection } from "@/components/deals/ChannelPartnerSection";
import { DealProfileSections, Field } from "@/components/deals/DealProfileSections";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DateRowInput } from "@/components/ui/date-row-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { FormError } from "@/components/ui/form-error";

interface Props {
  deal: Deal;
  /** Channel partner (reseller) business name — from the manager ledger row. */
  partnerName?: string | null;
  /** CHP-400: the deal owner is now rendered by ChannelPartnerSection from
   * `deal` (with the role suffix). Retained only so ManagerDealReview can keep
   * passing it without a prop-chain change; no longer read in this component. */
  leadName?: string | null;
  /** Called after a successful decision (post toast + 1.5s hold). Drawer →
   * close; standalone route → navigate to the dashboard. */
  onDone: () => void;
}

/** Hold the success toast before finishing — close the drawer / navigate
 * (CHP-233, mirrors CHP-223). */
const REDIRECT_HOLD_MS = 1500;

/** Tomorrow (yyyy-mm-dd) — the earliest valid revised expiry date. */
function tomorrowIso(): string {
  return new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
}

export function DealExtensionAdjudication({ deal, partnerName, onDone }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [action, setAction] = React.useState<"approve" | "edit" | "deny" | null>(null);
  const [revisedDate, setRevisedDate] = React.useState("");
  // CHP-413: optional ops-only note recorded against whichever decision the
  // reviewer makes (approve / edit&approve / deny). Persisted as an
  // `extension`-context internal note, CM/RA/RM-visible only.
  const [internalNotes, setInternalNotes] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  const partnerLabel = partnerName ?? "the channel partner";

  // Defer invalidation until the hold elapses so these actions stay mounted
  // for the full 1.5s rather than unmounting the moment the deal leaves
  // extension_pending (same pattern as the registration review).
  const holdThenDone = React.useCallback(() => {
    holdTimer.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onDone();
    }, REDIRECT_HOLD_MS);
  }, [onDone, queryClient]);

  const approve = useMutation({
    mutationFn: (revised: string | undefined) =>
      approveDealExtension(accessToken!, deal.id, {
        revised_close_date: revised || undefined,
        internal_notes: internalNotes.trim() || undefined,
      }),
    onSuccess: (_data, revised) => {
      setAction(null);
      setInternalNotes("");
      // Edit & Approve (a revised date was set) gets a distinct "Expiry date
      // updated" toast; a plain approve keeps "Extension approved" (CHP-357).
      toast(revised ? dealToasts.extensionExpiryUpdated() : dealToasts.extensionApproved());
      holdThenDone();
    },
    onError: () => setError("Approval failed. Refresh and try again."),
  });

  const deny = useMutation({
    mutationFn: (reason: string) =>
      denyDealExtension(accessToken!, deal.id, reason, internalNotes.trim() || undefined),
    onSuccess: () => {
      setAction(null);
      setInternalNotes("");
      toast(dealToasts.extensionDenied());
      holdThenDone();
    },
    onError: () => setError("Denial failed. Refresh and try again."),
  });

  if (deal.deal_status !== "extension_pending") return null;

  const anyPending = approve.isPending || deny.isPending;

  return (
    <div className="space-y-6">
      {/* Extension Request + Review Outcome (CHP-233). */}
      <Card>
        <CardHeader>
          <CardTitle>Extension Request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid gap-3 sm:grid-cols-2">
            <Field label="Current Expiry" value={deal.original_close_date ?? "—"} />
            <Field label="Requested Expiry" value={deal.extension_requested_date ?? "—"} />
          </dl>
          <dl>
            {/* Designer copy (CHP-357) — not a role-label token reference. */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <dt className="text-sm font-medium">Channel Partner&apos;s reasoning</dt>
            <dd className="mt-1 whitespace-pre-wrap rounded bg-muted/30 p-3 text-sm text-muted-foreground">
              {deal.extension_justification ?? "—"}
            </dd>
          </dl>
          {deal.extension_sla_breached && (
            <Badge variant="destructive" className="w-fit">
              48hr SLA breached
            </Badge>
          )}

          {/* CHP-413: optional ops-only note attached to whichever decision is
              made. Visible to ResetData staff only. Never shared with the
              channel partner. */}
          <div className="space-y-1">
            <Label htmlFor="extension-internal-notes">Internal notes (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Visible to ResetData staff only. Never shared with the channel partner.
            </p>
            <Textarea
              id="extension-internal-notes"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Visible to ResetData staff only. Never shared with the channel partner."
              className="w-full"
              disabled={anyPending}
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" onClick={() => setAction("approve")} disabled={anyPending}>
              Approve
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRevisedDate("");
                setAction("edit");
              }}
              disabled={anyPending}
            >
              Edit &amp; approve
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAction("deny")}
              disabled={anyPending}
            >
              Deny
            </Button>
          </div>

          {error && <FormError>{error}</FormError>}
        </CardContent>
      </Card>

      {/* The Deal — Channel Partner + the shared Client/Opportunity/Solution
          sections (CHP-233; conflict-check section intentionally removed). */}
      <Card>
        <CardHeader>
          <CardTitle>The Deal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* CHP-400 (audit fix): use the shared ChannelPartnerSection rather
              than an inline copy — de-duplicates the block and restores the
              deal-owner role suffix (e.g. "(BDM)") the inline copy dropped.
              Sourced from the full `deal` this component already receives. */}
          <ChannelPartnerSection deal={deal} />
          <DealProfileSections deal={deal} />
        </CardContent>
      </Card>

      {/* Approve — plain confirm. */}
      <ConfirmDialog
        open={action === "approve"}
        onOpenChange={(open) => !open && setAction(null)}
        title={`Approve extension for ${deal.client_name}?`}
        description={`This will update the deal expiry date for ${partnerLabel}.`}
        confirmLabel="Approve"
        pending={approve.isPending}
        onConfirm={() => {
          setError(null);
          approve.mutate(undefined);
        }}
      />

      {/* Edit & Approve — date picker. */}
      <Dialog open={action === "edit"} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit expiry date</DialogTitle>
            <DialogDescription>
              This will update the deal expiry date for {partnerLabel}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="revised-expiry">New expiry date</Label>
            {/* CHP-396: whole-row click opens the picker. */}
            <DateRowInput
              id="revised-expiry"
              value={revisedDate}
              min={tomorrowIso()}
              onChange={(e) => setRevisedDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} disabled={approve.isPending}>
              Cancel
            </Button>
            <Button
              disabled={!revisedDate}
              loading={approve.isPending}
              loadingLabel="Updating…"
              onClick={() => {
                setError(null);
                approve.mutate(revisedDate);
              }}
            >
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny — mandatory reason. */}
      <ConfirmDialog
        open={action === "deny"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Deny extension?"
        variant="destructive"
        confirmLabel="Confirm denial"
        reason={{
          // Designer copy (CHP-357) — not a role-label token reference.
          // eslint-disable-next-line no-restricted-syntax
          label: "Reason for Channel Partner",
          placeholder: "Explain why this request is being denied.",
          required: true,
          maxLength: 500,
        }}
        pending={deny.isPending}
        onConfirm={(reason) => {
          setError(null);
          deny.mutate(reason ?? "");
        }}
      />
    </div>
  );
}
